import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { useTranslation } from 'react-i18next'
import { 
  Target,
  TrendingUp,
  PiggyBank,
  Trophy,
  Lightbulb,
  Gift
} from 'lucide-react'

interface GoalSettingStepProps {
  onNext: () => void
  onSkip: () => void
}

export function GoalSettingStep({ onNext, onSkip }: GoalSettingStepProps) {
  const { t } = useTranslation()

  const goalExamples = [
    {
      icon: <PiggyBank className="h-6 w-6 text-[#439476]" />,
      title: t('onboarding.goalSetting.examples.emergency', 'Emergency Fund'),
      description: t('onboarding.goalSetting.examples.emergencyDesc', '3-6 months of expenses for unexpected situations'),
      amount: '$5,000',
      popular: true
    },
    {
      icon: <Gift className="h-6 w-6 text-[#439476]" />,
      title: t('onboarding.goalSetting.examples.vacation', 'Dream Vacation'),
      description: t('onboarding.goalSetting.examples.vacationDesc', 'Save for that special trip you\'ve been planning'),
      amount: '$3,000',
      popular: true
    },
    {
      icon: <Trophy className="h-6 w-6 text-[#439476]" />,
      title: t('onboarding.goalSetting.examples.gadget', 'New Gadget'),
      description: t('onboarding.goalSetting.examples.gadgetDesc', 'The latest phone, laptop, or tech you want'),
      amount: '$1,200',
      popular: false
    },
    {
      icon: <TrendingUp className="h-6 w-6 text-[#439476]" />,
      title: t('onboarding.goalSetting.examples.investment', 'Investment Fund'),
      description: t('onboarding.goalSetting.examples.investmentDesc', 'Build wealth for your future self'),
      amount: '$10,000',
      popular: false
    }
  ]

  const benefits = [
    {
      icon: <Target className="h-5 w-5 text-[#439476]" />,
      title: t('onboarding.goalSetting.benefits.motivation', 'Stay Motivated'),
      description: t('onboarding.goalSetting.benefits.motivationDesc', 'Visual progress keeps you focused')
    },
    {
      icon: <TrendingUp className="h-5 w-5 text-[#439476]" />,
      title: t('onboarding.goalSetting.benefits.progress', 'Track Progress'),
      description: t('onboarding.goalSetting.benefits.progressDesc', 'See exactly how close you are')
    },
    {
      icon: <Trophy className="h-5 w-5 text-[#439476]" />,
      title: t('onboarding.goalSetting.benefits.celebrate', 'Celebrate Wins'),
      description: t('onboarding.goalSetting.benefits.celebrateDesc', 'Get rewarded for milestones')
    }
  ]

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center space-y-4">
        <div className="relative mx-auto w-16 h-16 mb-4">
          <div className="absolute inset-0 bg-gradient-to-r from-[#439476] to-[#387565] rounded-xl opacity-20 animate-pulse"></div>
          <div className="relative bg-gradient-to-r from-[#439476] to-[#387565] rounded-xl p-3 shadow-lg">
            <Target className="h-10 w-10 text-white" />
          </div>
        </div>
        
        <h3 className="text-xl font-bold bg-gradient-to-r from-[#1C1C1C] to-[#383838] dark:from-[#E8E8E8] dark:to-[#CCCCCC] bg-clip-text text-transparent">
          {t('onboarding.goalSetting.title', 'Set Your Financial Goals')}
        </h3>
        <p className="text-muted-foreground text-lg">
          {t('onboarding.goalSetting.description', 'Turn your dreams into achievable savings goals with smart tracking and motivation.')}
        </p>
      </div>

      {/* Hero Illustration */}
      <Card className="p-6 bg-gradient-to-r from-[#439476]/5 to-[#387565]/5 border-[#439476]/20">
        <div className="text-center space-y-4">
          <div className="flex justify-center">
            <div className="relative w-32 h-4 bg-gray-200 rounded-full overflow-hidden">
              <div className="absolute left-0 top-0 h-full w-3/4 bg-gradient-to-r from-[#439476] to-[#387565] rounded-full"></div>
            </div>
          </div>
          <div className="space-y-1">
            <p className="font-medium text-[#439476]">75% Complete</p>
            <p className="text-sm text-muted-foreground">$3,750 of $5,000 saved</p>
            <p className="text-xs text-muted-foreground">Only $1,250 to go! 🎉</p>
          </div>
        </div>
      </Card>

      {/* Goal Examples */}
      <div className="space-y-4">
        <h4 className="font-medium text-base flex items-center gap-2">
          <Lightbulb className="h-5 w-5 text-[#439476]" />
          {t('onboarding.goalSetting.examplesTitle', 'Popular Savings Goals')}
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {goalExamples.map((goal, index) => (
            <Card 
              key={index} 
              className={`p-4 transition-all duration-200 hover:shadow-md ${
                goal.popular ? 'border-[#439476]/30 bg-[#439476]/5' : 'bg-white/80 dark:bg-[#182438]/80'
              }`}
            >
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-white dark:bg-[#182438] shadow-sm">
                  {goal.icon}
                </div>
                <div className="space-y-1 flex-1">
                  <div className="flex items-center justify-between">
                    <h5 className="font-medium text-sm">{goal.title}</h5>
                    <span className="text-sm font-semibold text-[#439476]">{goal.amount}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">{goal.description}</p>
                  {goal.popular && (
                    <span className="inline-block px-2 py-0.5 bg-[#439476]/10 text-[#439476] text-xs rounded-full">
                      {t('onboarding.goalSetting.popular', 'Popular')}
                    </span>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>

      {/* Benefits */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {benefits.map((benefit, index) => (
          <Card key={index} className="p-4 text-center bg-white/80 dark:bg-[#182438]/80">
            <div className="space-y-2">
              <div className="flex justify-center">
                <div className="p-2 rounded-lg bg-[#439476]/10">
                  {benefit.icon}
                </div>
              </div>
              <h5 className="font-medium text-sm">{benefit.title}</h5>
              <p className="text-xs text-muted-foreground">{benefit.description}</p>
            </div>
          </Card>
        ))}
      </div>

      {/* Call to Action */}
      <Card className="p-6 text-center space-y-6 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20 border-green-200 dark:border-green-800">
        <div className="flex justify-center">
          <div className="p-3 rounded-xl bg-green-100 dark:bg-green-900/20">
            <PiggyBank className="h-8 w-8 text-green-600 dark:text-green-400" />
          </div>
        </div>
        <h4 className="font-semibold text-green-900 dark:text-green-100">
          {t('onboarding.goalSetting.ctaTitle', 'Start Your First Savings Goal?')}
        </h4>
        <p className="text-green-700 dark:text-green-300 text-sm">
          {t('onboarding.goalSetting.ctaDescription', 'Set a goal now to start building positive saving habits, or skip and add goals later from the Goals page.')}
        </p>
        <div className="flex items-center justify-center gap-4">
          <Button
            variant="outline"
            onClick={onSkip}
            className="text-green-700 dark:text-green-300 border-green-300 dark:border-green-700 hover:bg-green-50 dark:hover:bg-green-900/20"
          >
            {t('onboarding.goalSetting.skipGoals', 'Skip Goals')}
          </Button>
          <Button
            onClick={onNext}
            className="bg-green-600 hover:bg-green-700 text-white px-6"
          >
            {t('onboarding.goalSetting.setGoal', 'Set First Goal')}
          </Button>
        </div>
      </Card>

    </div>
  )
} 